import React, { useState, useEffect } from 'react';
import { ChapterAnalysisResponse, AIUsageStatus, UpgradePromptData, SubscriptionTier } from '../types/api';
import apiClient from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import UpgradePrompt from './UpgradePrompt';
import './ChapterAnalysis.css';

interface ChapterAnalysisProps {
  chapterId: string;
  chapterTitle: string;
  chapterContent: string;
  onUsageUpdate?: () => void;
}

const ChapterAnalysis: React.FC<ChapterAnalysisProps> = ({
  chapterId,
  chapterTitle,
  chapterContent,
  onUsageUpdate
}) => {
  const [analysis, setAnalysis] = useState<ChapterAnalysisResponse | null>(null);
  const [streamingText, setStreamingText] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usageStatus, setUsageStatus] = useState<AIUsageStatus | null>(null);
  const [showFullAnalysis, setShowFullAnalysis] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Load usage status on component mount
  useEffect(() => {
    loadUsageStatus();
  }, []);

  const loadUsageStatus = async () => {
    try {
      const status = await apiClient.getAIUsageStatus();
      setUsageStatus(status);
    } catch (error) {
      console.error('Failed to load AI usage status:', error);
    }
  };

  const handleAnalyzeChapter = async () => {
    if (!chapterContent.trim()) {
      setError('Please write some content in your chapter before analyzing.');
      return;
    }

    if (usageStatus && usageStatus.current_usage >= usageStatus.limit) {
      setShowUpgradePrompt(true);
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setStreamingText('');
    setAnalysis(null);
    setShowFullAnalysis(true);

    try {
      // Use streaming endpoint
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/api/ai/analyze/${chapterId}/stream`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = 'Failed to analyze chapter';

        if (response.status === 429) {
          errorMessage = errorData.detail?.message || 'AI usage limit exceeded';
          setShowUpgradePrompt(true);
        } else if (errorData.detail) {
          errorMessage = typeof errorData.detail === 'string' ? errorData.detail : errorData.detail.message || 'An error occurred';
        }

        throw new Error(errorMessage);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('Stream not available');
      }

      let buffer = '';
      let fullText = '';
      let metadata: any = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.token) {
                fullText += data.token;
                setStreamingText(fullText);
              }

              if (data.done) {
                metadata = data.metadata;
                // Create final analysis response
                setAnalysis({
                  analysis: fullText,
                  usage_info: data.usage_info || {},
                  metadata: data.metadata || { model_used: 'unknown', analysis_type: 'chapter_analysis' }
                });
              }

              if (data.error) {
                throw new Error(data.error);
              }
            } catch (parseError) {
              console.warn('Failed to parse SSE data:', line);
            }
          }
        }
      }

      // Update usage status
      await loadUsageStatus();

      // Notify parent component of usage update
      if (onUsageUpdate) {
        onUsageUpdate();
      }
    } catch (error: any) {
      console.error('Analysis failed:', error);
      const errorMessage = error.message || 'Failed to analyze chapter. Please try again.';
      setError(errorMessage);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Enhanced markdown-style formatter
  const formatAnalysisText = (text: string) => {
    return text.split('\n').map((line, index) => {
      // Headers (### Header)
      if (line.startsWith('###')) {
        return <h3 key={index} className="analysis-h3">{line.replace(/^###\s*/, '')}</h3>;
      }
      if (line.startsWith('##')) {
        return <h2 key={index} className="analysis-h2">{line.replace(/^##\s*/, '')}</h2>;
      }
      if (line.startsWith('#')) {
        return <h1 key={index} className="analysis-h1">{line.replace(/^#\s*/, '')}</h1>;
      }

      // Bold text (**text**)
      if (line.includes('**')) {
        const parts = line.split(/(\*\*.*?\*\*)/g);
        return (
          <p key={index} className="analysis-paragraph">
            {parts.map((part, i) => {
              if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={i}>{part.replace(/\*\*/g, '')}</strong>;
              }
              return formatInlineMarkdown(part, i);
            })}
          </p>
        );
      }

      // Bullet points (- item or * item)
      if (line.match(/^[\-\*]\s+/)) {
        return (
          <li key={index} className="analysis-list-item">
            {formatInlineMarkdown(line.replace(/^[\-\*]\s+/, ''))}
          </li>
        );
      }

      // Empty lines
      if (!line.trim()) {
        return <br key={index} />;
      }

      // Regular paragraphs
      return (
        <p key={index} className="analysis-paragraph">
          {formatInlineMarkdown(line)}
        </p>
      );
    });
  };

  // Helper to format inline markdown (italic, code, etc.)
  const formatInlineMarkdown = (text: string, keyPrefix: number = 0): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    let currentText = text;
    let key = keyPrefix;

    // Handle italic text (*text* or _text_)
    const italicRegex = /(\*[^*]+\*|_[^_]+_)/g;
    const italicParts = currentText.split(italicRegex);

    return italicParts.map((part, i) => {
      if ((part.startsWith('*') && part.endsWith('*') && !part.startsWith('**')) ||
        (part.startsWith('_') && part.endsWith('_') && !part.startsWith('__'))) {
        return <em key={`${keyPrefix}-${i}`}>{part.slice(1, -1)}</em>;
      }
      return part;
    });
  };

  return (
    <div className="chapter-analysis">
      {/* Header */}
      <div className="analysis-header">
        <div className="chapter-info">
          <h4>{chapterTitle}</h4>
          <p className="chapter-stats">
            {chapterContent.split(' ').length} words
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="analysis-error">
          <p>{error}</p>
        </div>
      )}

      {/* Analyze Button */}
      <div className="analysis-actions">
        <button
          onClick={handleAnalyzeChapter}
          disabled={isAnalyzing}
          className={`analyze-btn ${isAnalyzing ? 'analyzing' : ''}`}
        >
          {isAnalyzing ? (
            <>
              <LoadingSpinner size="sm" />
              <span>Analyzing...</span>
            </>
          ) : (
            <>
              <span>Analyse Chapter</span>
              <small style={{ display: 'block', fontSize: '0.75rem', fontWeight: 'normal', marginTop: '2px' }}>
                (1 AI credit)
              </small>
            </>
          )}
        </button>
      </div>

      {/* Analysis Results - Show streaming text or final analysis */}
      {(analysis || streamingText) && (
        <div className="analysis-results">
          <div className="results-header">
            <h4>Analysis Results {isAnalyzing && <span className="streaming-indicator">● Live</span>}</h4>
            <div className="results-actions">
              {!isAnalyzing && (
                <button
                  onClick={() => setShowModal(true)}
                  className="view-modal-btn"
                  title="View in full screen"
                >
                  🔍 Full Screen
                </button>
              )}
              <button
                onClick={() => setShowFullAnalysis(!showFullAnalysis)}
                className="toggle-btn"
              >
                {showFullAnalysis ? '▲ Collapse' : '▼ Expand'}
              </button>
            </div>
          </div>

          {/* Analysis Content */}
          <div className={`analysis-text ${showFullAnalysis ? 'expanded' : 'collapsed'}`}>
            <div className="analysis-formatted-content">
              {formatAnalysisText(analysis?.analysis || streamingText)}
            </div>
          </div>

          {!showFullAnalysis && (analysis?.analysis || streamingText).length > 300 && (
            <button
              onClick={() => setShowFullAnalysis(true)}
              className="read-more-btn"
            >
              📖 Read Full Analysis
            </button>
          )}
        </div>
      )}

      {/* Modal View */}
      {showModal && analysis && (
        <div className="analysis-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="analysis-modal" onClick={(e) => e.stopPropagation()}>
            <div className="analysis-modal-header">
              <div>
                <h2>Chapter Analysis</h2>
                <p className="modal-chapter-title">{chapterTitle}</p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="modal-close-btn"
                title="Close"
              >
                ✕
              </button>
            </div>
            <div className="analysis-modal-content">
              <div className="analysis-formatted-content">
                {formatAnalysisText(analysis.analysis)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Prompt */}
      {showUpgradePrompt && usageStatus && (
        <UpgradePrompt
          data={{
            type: 'ai_usage_limit',
            current_plan: usageStatus.subscription_tier as SubscriptionTier,
            suggested_plan: usageStatus.subscription_tier?.toLowerCase() === 'premium' ? 'ai_credits' as SubscriptionTier : 'premium' as SubscriptionTier,
            message: `You've reached your AI usage limit of ${usageStatus.limit} requests. Upgrade to get more AI requests and continue using analysis features.`,
            limits: {
              current: usageStatus.current_usage,
              limit: usageStatus.limit
            },
            features: [
              "AI-powered chapter analysis",
              "Sentiment and tone analysis",
              "Theme and character tracking",
              "Writing improvement suggestions"
            ]
          }}
          onClose={() => setShowUpgradePrompt(false)}
          onUpgrade={() => {
            window.location.href = '/subscription';
          }}
        />
      )}
    </div>
  );
};

export default ChapterAnalysis;
